import React, { useRef } from 'react';
import { FormField, FormFieldLabel } from 'styles/common.styles';
import styled from 'styled-components';
import { theme } from 'styles/theme';

// 파일 선택 영역 스타일
const FileInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: ${props => props.width || 'auto'};
`;

const FileInput = styled.input`
  display: none; /* 기본 파일 input 숨기기 */
`;

const FileInputButton = styled.button`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  background-color: ${theme.colors.white};
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  
  &:hover {
    background-color: ${theme.colors.background.light};
    border-color: ${theme.colors.primary};
  }
  
  &:disabled {
    background-color: ${theme.colors.background.light};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const FileNameDisplay = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileListContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 100px;
  overflow-y: auto;
`;

const FileNameItem = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0;
`;

const FileNameText = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemoveButton = styled.button`
  padding: 2px 8px;
  border: none;
  background-color: transparent;
  color: ${theme.colors.status.error};
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  
  &:hover {
    color: ${theme.colors.status.error};
    text-decoration: underline;
  }
`;

/**
 * File 타입 필드 컴포넌트
 * 
 * @param {Object} props
 * @param {string} props.name - 필드 이름
 * @param {string} props.label - 필드 라벨
 * @param {File|string} props.value - 선택된 파일 또는 파일명
 * @param {string} props.width - 필드 너비
 * @param {string} props.labelWidth - 라벨 너비
 * @param {string} props.fieldWidth - FormField 전체 너비
 * @param {boolean} props.readonly - 읽기 전용 여부
 * @param {boolean} props.hasError - 에러 상태
 * @param {string} props.accept - 허용할 파일 타입 (예: "image/*", ".pdf,.doc")
 * @param {boolean} props.multiple - 다중 파일 선택 여부
 * @param {Function} props.onChange - 값 변경 핸들러
 * @param {Object} props.fieldProps - 추가 필드 속성
 */
const FileField = ({
  name,
  label,
  value = null,
  width,
  labelWidth,
  fieldWidth,
  showColon = false,
  readonly = false,
  accept,
  multiple = false,
  onChange,
  fieldProps = {},
}) => {
  const fileInputRef = useRef(null);

  // 파일명 가져오기 (단일 파일)
  const getFileName = () => {
    if (!value) return '';
    if (value instanceof File) {
      return value.name;
    }
    if (typeof value === 'string') {
      // 파일 경로에서 파일명만 추출
      return value.split('/').pop() || value.split('\\').pop() || value;
    }
    return '';
  };

  // 파일명 배열 가져오기 (다중 파일)
  const getFileNames = () => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.map(file => {
        if (file instanceof File) {
          return file.name;
        }
        if (typeof file === 'string') {
          return file.split('/').pop() || file.split('\\').pop() || file;
        }
        return '';
      }).filter(name => name);
    }
    // 단일 파일인 경우
    const fileName = getFileName();
    return fileName ? [fileName] : [];
  };

  // 파일 배열 가져오기 (File 객체 또는 문자열 배열)
  const getFileArray = () => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value;
    }
    // 단일 파일인 경우 배열로 변환
    return value ? [value] : [];
  };

  const fileNames = getFileNames();
  const fileArray = getFileArray();
  const isMultiple = multiple && fileNames.length > 0;

  // 파일 선택 버튼 클릭
  const handleButtonClick = () => {
    if (!readonly && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // 파일 선택 변경
  const handleFileChange = (e) => {
    if (readonly || !onChange) return;
    
    const files = e.target.files;
    if (files && files.length > 0) {
      if (multiple) {
        // 다중 파일 선택 - 기존 파일에 새 파일 추가
        const newFiles = Array.from(files);
        const existingFiles = Array.isArray(value) ? value : (value ? [value] : []);
        const combinedFiles = [...existingFiles, ...newFiles];
        
        const syntheticEvent = {
          target: {
            name: name,
            value: combinedFiles,
            files: files,
          },
        };
        onChange(syntheticEvent);
      } else {
        // 단일 파일 선택
        const syntheticEvent = {
          target: {
            name: name,
            value: files[0],
            files: files,
          },
        };
        onChange(syntheticEvent);
      }
    }
    
    // input 초기화 (같은 파일을 다시 선택할 수 있도록)
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 파일 제거 (전체 또는 특정 인덱스)
  const handleRemove = (e, removeIndex = null) => {
    e.stopPropagation();
    if (readonly || !onChange) return;
    
    if (multiple && Array.isArray(value) && removeIndex !== null) {
      // 다중 파일 선택 시 특정 파일만 제거
      const newFileArray = fileArray.filter((_, index) => index !== removeIndex);
      
      const syntheticEvent = {
        target: {
          name: name,
          value: newFileArray.length > 0 ? newFileArray : null,
          files: null,
        },
      };
      onChange(syntheticEvent);
    } else {
      // 단일 파일 또는 전체 제거
      const syntheticEvent = {
        target: {
          name: name,
          value: null,
          files: null,
        },
      };
      onChange(syntheticEvent);
      
      // input 초기화
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <FormField
      width={fieldWidth || 'auto'}
    >
      {label && (
        <FormFieldLabel width={labelWidth} >{showColon ? `${label}:` : label}</FormFieldLabel>
      )}
      <FileInputWrapper width={width || 'auto'}>
        <FileInput
          ref={fileInputRef}
          type="file"
          name={name}
          accept={accept}
          multiple={multiple}
          disabled={readonly}
          onChange={handleFileChange}
          {...fieldProps}
        />
        <FileInputButton
          type="button"
          onClick={handleButtonClick}
          disabled={readonly}
        >
          파일 선택
        </FileInputButton>
        {fileNames.length > 0 && (
          <>
            {isMultiple ? (
              // 다중 파일 선택 시 파일 목록 표시
              <FileListContainer>
                {fileNames.map((fileName, index) => (
                  <FileNameItem key={index}>
                    <FileNameText title={fileName}>
                      {fileName}
                    </FileNameText>
                    {!readonly && (
                      <RemoveButton 
                        type="button" 
                        onClick={(e) => {
                          e.stopPropagation();
                          // 특정 파일만 제거 (인덱스 전달)
                          handleRemove(e, index);
                        }}
                      >
                        삭제
                      </RemoveButton>
                    )}
                  </FileNameItem>
                ))}
              </FileListContainer>
            ) : (
              // 단일 파일 선택 시
              <>
                <FileNameDisplay title={fileNames[0]}>
                  {fileNames[0]}
                </FileNameDisplay>
                {!readonly && (
                  <RemoveButton type="button" onClick={handleRemove}>
                    삭제
                  </RemoveButton>
                )}
              </>
            )}
          </>
        )}
      </FileInputWrapper>
    </FormField>
  );
};

export default FileField;

